import React, { useState, useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { browserHistory } from 'react-router';
import {
  Typography,
  Divider,
  Card,
  Space,
  Button,
  Menu,
  Modal,
  Tooltip,
  message,
  Spin,
} from 'antd';
import { connect } from 'react-redux';
import { getTranslatedString } from 'helpers/i18n';
import {
  ApplicationUIContext,
  ButtonGroup,
  Statistic,
  Drawer,
  DrawerContent,
  useDrawerState,
} from '@yuhuinc/ui-library';
import { apiPost, apiPutRequest } from 'helpers/ApiHelper';
import { showFailure } from 'actions/notificationActions';

import { CurrentApplicationContext } from '../ApplicationOverview';
import RemoveApplicant from './applicants/RemoveApplicant';
import BackgroundChecks from './applicants/BackgroundChecks';
import Flags from './applicants/Flags';
import Documents from './applicants/Documents';
import Insurance from './applicants/Insurance';
import ApplicantResponses from './applicants/ApplicantResponses';

import { CardSectionTitle, CardContentWithDividers, StyledHeader } from '../common/Styles';
import NewApplicantErrorAlert from '../common/NewApplicantError';
import NewOrEditApplicant from '../common/NewOrEditApplicant';
import ApplicantStatus from '../common/ApplicantStatus';
import Stats from '../common/Stats';
import { phaseTwoWip } from '../common/actions/WipModal';
import { ContactMethod } from '../additional_information/ContactMethod';
import {
  buildInitialValuesForSelectedApplicant,
  isThereOnlyOneApplicant,
  isOccupant,
  isApplicantInvitable,
  isApplicantRemindable,
  notOccupant,
} from '../common/helpers';
import { featureEnabled } from 'helpers/ToggleBot';

const { Text } = Typography;

const CardHeader = ({ title, secondary }) => {
  const { isMobile } = useContext(ApplicationUIContext);

  return (
    <div>
      <CardSectionTitle style={{ marginBottom: isMobile ? 8 : 4 }}>{title}</CardSectionTitle>
      <Text type="secondary">{secondary}</Text>
    </div>
  );
};

const cotenantSubstatusesValidToMarkAsSubmitted = [
  'not_invited',
  'not_started',
  'in_progress',
  'adding_details',
];
const applicationSubstatusesValidToMarkAsSubmitted = [
  'draft',
  'sent',
  'in_progress',
  'details_required',
  'details_added',
  'changes_required',
  'change_consent_required',
  'change_consent_submitted',
];

const Applicants = ({ showFailure, selectedApplicantId }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const currentApplication = useContext(CurrentApplicationContext);
  const [applicantError, setApplicantError] = useState(null);
  const [, editApplicantInfoDrawer] = useDrawerState(false);
  const [, removeApplicantDrawer] = useDrawerState(false);
  const queryClient = useQueryClient();

  const saveApplicantMutation = useMutation({
    mutationFn: (data) => {
      return apiPutRequest(
        `/landlords/application_management/applications/${currentApplication.id}/applicants/${data.id}`,
        data
      );
    },
    onSuccess() {
      message.success(
        getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.overview.applicants.editApplicantInfo.success'
        )
      );
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError() {
      // TODO: Ask Product for a proper error message
      message.error(
        getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.overview.applicants.editApplicantInfo.error'
        )
      );
    },
  });

  const markApplicantAsSubmittedMutation = useMutation({
    mutationFn: (applicant) =>
      apiPutRequest(
        `/landlords/application_management/applications/${currentApplication.id}/applicants/${applicant.id}/mark_as_submitted`
      ),
    onSuccess() {
      message.success(
        getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.markSubmitted.success'
        )
      );
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError() {
      message.error(
        getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.markSubmitted.error'
        )
      );
    },
  });

  const inviteApplicantToSubmitMutation = useMutation({
    mutationFn: (applicant) =>
      apiPost(
        `/landlords/application_management/applications/${currentApplication.id}/applicants/${applicant.id}/invite`
      ),
    onSuccess() {
      message.success(
        getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.invite.success'
        )
      );
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError(error) {
      showFailure(error);
    },
  });

  const remindApplicantToSubmitMutation = useMutation({
    mutationFn: (applicant) =>
      apiPost(
        `/landlords/application_management/applications/${currentApplication.id}/applicants/${applicant.id}/remind`
      ),
    onSuccess() {
      message.success(
        getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.remind.success'
        )
      );
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError(error) {
      showFailure(error);
    },
  });

  const applicant = currentApplication?.applicants?.find(
    (applicant) => applicant.custom_id === selectedApplicantId
  );

  if (!applicant) {
    return <Spin />;
  }

  const onSaveApplicant = (values) => {
    saveApplicantMutation.mutate(values);
  };

  const removeApplicantOverlay = () => (
    <Menu
      items={[
        {
          key: 'removeFromApplication',
          label: getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.removeFromApplication'
          ),
          onClick: () => removeApplicantDrawer.open(),
          disabled: isThereOnlyOneApplicant(currentApplication),
        },
      ]}
    />
  );

  const maybeRenderMenuWithTooltip = () =>
    isThereOnlyOneApplicant(currentApplication) ? (
      <Tooltip
        title={getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.removeFromApplication.cannotRemoveSoleApplicant'
        )}
        placement="bottom"
      >
        {removeApplicantOverlay()}
      </Tooltip>
    ) : (
      removeApplicantOverlay()
    );

  const nonOccupantHeaderButtons = [
    isApplicantInvitable(applicant) && {
      label: getTranslatedString(
        'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.invite'
      ),
      type: 'primary',
      onClick: () => inviteApplicantToSubmitMutation.mutate(applicant),
      loading: inviteApplicantToSubmitMutation.isLoading,
    },
    isApplicantRemindable(applicant) && {
      label: getTranslatedString(
        'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.remind'
      ),
      type: 'primary',
      onClick: () => phaseTwoWip(() => remindApplicantToSubmitMutation.mutate(applicant)),
      loading: remindApplicantToSubmitMutation.isLoading,
    },
    {
      label: getTranslatedString(
        'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.print'
      ),
      onClick: () => {
        phaseTwoWip(() => {});
      },
    },
  ].filter((visibleButton) => visibleButton);

  const isPossibleToMarkApplicantAsSubmitted =
    !isOccupant(applicant) &&
    cotenantSubstatusesValidToMarkAsSubmitted.includes(applicant.lifecycle?.substatus) &&
    applicationSubstatusesValidToMarkAsSubmitted.includes(currentApplication.lifecycle?.substatus);

  if (isPossibleToMarkApplicantAsSubmitted) {
    nonOccupantHeaderButtons.push({
      label: getTranslatedString(
        'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.markSubmitted'
      ),
      onClick: () => {
        Modal.confirm({
          title: getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.markSubmitted.confirmationModal.title'
          ),
          onOk: () => markApplicantAsSubmittedMutation.mutate(applicant),
          okText: getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.markSubmitted'
          ),
          okCancel: true,
          cancelText: getTranslatedString('cancel'),
          content: getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.markSubmitted.confirmationModal.content'
          ),
          destroyOnClose: true,
        });
      },
    });
  }

  const goToEditResponses = () => {
    browserHistory.push(
      `/companies/application_templates/${applicant.application_template_id}/wizard/edit?applicant_id=${applicant.id}&application_id=${currentApplication.id}`
    );
  };

  return (
    <Space direction="vertical" size={16} style={{ flex: 'auto', width: '100%' }}>
      <Card>
        <StyledHeader>
          <CardHeader
            title={applicant.full_name}
            secondary={getTranslatedString(
              `landlordPortal.applicationManagement.applicationOverview.overview.applicants.applicantTypes.${applicant.applicant_type}`
            )}
          />

          {isOccupant(applicant) ? (
            <Button onClick={() => removeApplicantDrawer.open()}>
              {getTranslatedString(
                'landlordPortal.applicationManagement.applicationOverview.overview.applicants.actions.removeFromApplication'
              )}
            </Button>
          ) : (
            <ButtonGroup
              buttons={nonOccupantHeaderButtons}
              dropdownOverlay={maybeRenderMenuWithTooltip()}
            />
          )}
        </StyledHeader>
        {notOccupant(applicant) && (
          <>
            <Divider />
            <StyledHeader>
              <Stats
                items={[
                  {
                    title: getTranslatedString(
                      'landlordPortal.applicationManagement.applicationOverview.overview.applicants.status'
                    ),
                    value: applicant,
                    formatter: (applicant) => <ApplicantStatus applicant={applicant} />,
                  },
                  {
                    title: getTranslatedString(
                      'landlordPortal.applicationManagement.applicationOverview.overview.applicants.flags'
                    ),
                    value: applicant.flags,
                  },
                ]}
              />
              <Button
                style={{ alignSelf: 'center' }}
                onClick={() => {
                  phaseTwoWip(editApplicantInfoDrawer.open);
                }}
              >
                {getTranslatedString(
                  'landlordPortal.applicationManagement.applicationOverview.overview.applicants.editApplicantInfo'
                )}
              </Button>
            </StyledHeader>
          </>
        )}
      </Card>

      {notOccupant(applicant) && (
        <Card>
          <CardContentWithDividers>
            <BackgroundChecks applicant={applicant} />
            {!featureEnabled('4-0-phase-2-wip') && <Flags />}
            <Documents />
            <Insurance />
            <Button onClick={() => goToEditResponses()}>
              {getTranslatedString(
                'landlordPortal.applicationManagement.applicationOverview.overview.applicantResponses.edit'
              )}
            </Button>
            <ApplicantResponses applicationId={currentApplication.id} applicantId={applicant.id} />
          </CardContentWithDividers>
        </Card>
      )}

      <Drawer
        title={getTranslatedString(
          'landlordPortal.applicationManagement.inviteLeadToApply.editApplicantInfo'
        )}
        visible={editApplicantInfoDrawer.isVisible}
        closable
        destroyOnClose
        onClose={() => {
          setApplicantError(null);
          editApplicantInfoDrawer.close();
        }}
      >
        <DrawerContent>
          {applicantError && <NewApplicantErrorAlert {...applicantError} isMobile={isMobile} />}
          <NewOrEditApplicant
            initialValues={buildInitialValuesForSelectedApplicant(applicant)}
            extraFormItems={<ContactMethod />}
            onSubmit={(updatedApplicant) => {
              onSaveApplicant(updatedApplicant);
              editApplicantInfoDrawer.close();
            }}
            onError={setApplicantError}
            onCancel={editApplicantInfoDrawer.close}
          />
        </DrawerContent>
      </Drawer>
      <RemoveApplicant
        applicantToRemove={applicant}
        removeApplicantDrawer={removeApplicantDrawer}
      />
    </Space>
  );
};

export default connect(null, {
  showFailure,
})(Applicants);
